import { useCallback } from 'react'
import { QueryKey, useQueryClient } from 'react-query'

import { useInvalidateBills } from '@features/bills/hooks/useInvalidateBills'
import { getBankLinesQueryKey } from '@modules-deprecated/bankReconciliation/utils/getBankLinesQueryKey'

import { CustomEvent } from '../enums/customEvent'
import { QueryKeys } from '../enums/queryKeys'
import { CustomEventType, useCustomEventListener } from './useCustomEventListener'

type SubjectType = 'invoice' | 'bill'
interface PaymentChangedEventDetail {
  subjectId?: string
  subjectType?: SubjectType
}

interface BankAccountCsvImportCompletedEventDetail {
  accountId?: string
}

const getInvalidateQueriesByTypeMap = (id: string): Record<SubjectType, QueryKey> => ({
  invoice: [QueryKeys.Invoice, id],
  bill: [QueryKeys.Bill, id],
})

export const useInvalidateQueriesCustomListener = () => {
  const queryClient = useQueryClient()
  const { invalidateBills } = useInvalidateBills()

  const handlePaymentChanged = useCallback(
    (event: CustomEventType<PaymentChangedEventDetail>) => {
      const subjectId = event.detail?.subjectId
      const subjectType = event.detail?.subjectType

      if (subjectId && subjectType) {
        const invalidateKey = getInvalidateQueriesByTypeMap(subjectId)[subjectType]
        queryClient.invalidateQueries(invalidateKey)
      }

      invalidateBills()
    },
    [queryClient, invalidateBills],
  )

  const handleBankAccountCsvImportCompleted = useCallback(
    (event: CustomEventType<BankAccountCsvImportCompletedEventDetail>) => {
      const bankLinesQueryKey = getBankLinesQueryKey({ accountId: event.detail?.accountId })

      queryClient.invalidateQueries(bankLinesQueryKey)
    },
    [queryClient],
  )

  useCustomEventListener<PaymentChangedEventDetail>(CustomEvent.PaymentUpdated, handlePaymentChanged)
  useCustomEventListener<PaymentChangedEventDetail>(CustomEvent.PaymentSaved, handlePaymentChanged)
  useCustomEventListener<BankAccountCsvImportCompletedEventDetail>(
    CustomEvent.BankAccountCsvImportCompleted,
    handleBankAccountCsvImportCompleted,
  )
}
